import { http } from '@/app/lib/http/client'

export interface ClientAddress {
  street: string
  exteriorNumber: string
  interiorNumber?: string
  postalCode: string
  neighborhood: string
  municipality: string
  city: string
  state: string
  country: string
  reference?: string
}

export interface PurchaseMetrics {
  purchasesCount: number
  netPurchasesTotal: number
  averageTicket: number
  lastPurchaseAt: string | null
}

export interface LoyaltyTier {
  code: 'BRONZE' | 'SILVER' | 'GOLD'
  name: string
  medal: string
}

export interface ClientLoyalty {
  pointsBalance: number
  lifetimePoints: number
  creditBalance: number
  currentTier: LoyaltyTier
  lifetimeTier: LoyaltyTier
}

export interface ClientItem {
  id: number
  name: string
  emails: string[]
  phones: string[]
  email?: string | null
  phone?: string | null
  address?: ClientAddress | null
  notes?: string | null
  avatarUrl?: string | null
  purchaseMetrics: PurchaseMetrics
  loyalty?: ClientLoyalty
  is_active: boolean
  created_at?: string
  updated_at?: string
}

export interface ClientPurchase {
  id: number
  ticketNumber: string
  status: string
  branchName: string
  itemsCount: number
  originalTotal: number
  refundedTotal: number
  netTotal: number
  completedAt: string
}

export interface ClientSummary {
  customersCount: number
  purchasesCount: number
  netPurchasesTotal: number
}

export interface ClientListResult {
  data: ClientItem[]
  currentPage: number
  lastPage: number
  perPage: number
  total: number
  from?: number
  to?: number
}

export interface ClientPurchasesResult {
  data: ClientPurchase[]
  currentPage: number
  lastPage: number
  perPage: number
  total: number
  from?: number
  to?: number
}

export interface CreateClientPayload {
  name: string
  emails: string[]
  phones: string[]
  address: ClientAddress | null
  notes?: string
  avatar?: File | null
  is_active?: boolean
}

export interface UpdateClientPayload {
  name?: string
  emails?: string[]
  phones?: string[]
  address?: ClientAddress | null
  notes?: string | null
  avatar?: File | null
  remove_avatar?: boolean
}

interface BackendAddress {
  street: string
  exterior_number: string
  interior_number?: string | null
  postal_code: string
  neighborhood: string
  municipality: string
  city: string
  state: string
  country: string
  reference?: string | null
}

interface BackendClient {
  id: number
  name: string
  email?: string | null
  phone?: string | null
  emails?: string[]
  phones?: string[]
  address?: BackendAddress | null
  notes?: string | null
  avatar_url?: string | null
  is_active: boolean
  purchase_metrics?: {
    purchases_count?: number
    net_purchases_total?: string | number
    average_ticket?: string | number
    last_purchase_at?: string | null
  }
  loyalty?: {
    points_balance?: number
    lifetime_points?: number
    credit_balance?: string | number
    current_tier?: LoyaltyTier
    lifetime_tier?: LoyaltyTier
  }
  created_at?: string
  updated_at?: string
}

interface BackendPurchase {
  id: number
  ticket_number: string
  status: string
  sucursal?: { name?: string } | null
  items_count?: number
  original_total?: string | number
  refunded_total?: string | number
  net_total?: string | number
  completed_at: string
}

const numberValue = (value: unknown) =>
  Number.parseFloat(String(value ?? 0)) || 0

function mapAddress(address?: BackendAddress | null): ClientAddress | null {
  if (!address) return null

  return {
    street: address.street,
    exteriorNumber: address.exterior_number,
    interiorNumber: address.interior_number || '',
    postalCode: address.postal_code,
    neighborhood: address.neighborhood,
    municipality: address.municipality,
    city: address.city,
    state: address.state,
    country: address.country,
    reference: address.reference || '',
  }
}

function mapClient(client: BackendClient): ClientItem {
  const metrics = client.purchase_metrics || {}
  const loyalty = client.loyalty || {}
  const bronze: LoyaltyTier = { code: 'BRONZE', name: 'Bronce', medal: '🥉' }

  return {
    id: client.id,
    name: client.name,
    email: client.email,
    phone: client.phone,
    emails: client.emails || (client.email ? [client.email] : []),
    phones: client.phones || (client.phone ? [client.phone] : []),
    address: mapAddress(client.address),
    notes: client.notes,
    avatarUrl: client.avatar_url,
    purchaseMetrics: {
      purchasesCount: Number(metrics.purchases_count || 0),
      netPurchasesTotal: numberValue(metrics.net_purchases_total),
      averageTicket: numberValue(metrics.average_ticket),
      lastPurchaseAt: metrics.last_purchase_at || null,
    },
    loyalty: {
      pointsBalance: Number(loyalty.points_balance || 0),
      lifetimePoints: Number(loyalty.lifetime_points || 0),
      creditBalance: numberValue(loyalty.credit_balance),
      currentTier: loyalty.current_tier || bronze,
      lifetimeTier: loyalty.lifetime_tier || bronze,
    },
    is_active: Boolean(client.is_active),
    created_at: client.created_at,
    updated_at: client.updated_at,
  }
}

function appendPayload(
  formData: FormData,
  payload: CreateClientPayload | UpdateClientPayload,
) {
  if (payload.name !== undefined) formData.append('name', payload.name)
  if (payload.notes !== undefined) formData.append('notes', payload.notes ?? '')
  if ('is_active' in payload && payload.is_active !== undefined) {
    formData.append('is_active', payload.is_active ? '1' : '0')
  }
  if (payload.emails !== undefined) {
    if (payload.emails.length === 0) formData.append('emails', '[]')
    else payload.emails.forEach((email) => formData.append('emails[]', email))
  }
  if (payload.phones !== undefined) {
    if (payload.phones.length === 0) formData.append('phones', '[]')
    else payload.phones.forEach((phone) => formData.append('phones[]', phone))
  }

  if (payload.address === null) {
    formData.append('address', '')
  } else if (payload.address) {
    const address: Record<string, string | undefined> = {
      street: payload.address.street,
      exterior_number: payload.address.exteriorNumber,
      interior_number: payload.address.interiorNumber,
      postal_code: payload.address.postalCode,
      neighborhood: payload.address.neighborhood,
      municipality: payload.address.municipality,
      city: payload.address.city,
      state: payload.address.state,
      country: payload.address.country,
      reference: payload.address.reference,
    }
    Object.entries(address).forEach(([key, value]) => {
      formData.append(`address[${key}]`, value || '')
    })
  }

  if (payload.avatar instanceof File) formData.append('avatar', payload.avatar)
  if ('remove_avatar' in payload && payload.remove_avatar !== undefined) {
    formData.append('remove_avatar', payload.remove_avatar ? '1' : '0')
  }
}

export const clientsService = {
  async getItems(params?: {
    per_page?: number
    search?: string
    page?: number
    date_from?: string
    date_to?: string
  }): Promise<ClientListResult> {
    const response = await http.get('/customers', { params })
    const paginator = response.data?.data || {}

    return {
      data: ((paginator.data || []) as BackendClient[]).map(mapClient),
      currentPage: paginator.current_page || 1,
      lastPage: paginator.last_page || 1,
      perPage: paginator.per_page || params?.per_page || 15,
      total: paginator.total || 0,
      from: paginator.from,
      to: paginator.to,
    }
  },

  async getItem(id: number): Promise<ClientItem> {
    const response = await http.get(`/customers/${id}`)
    return mapClient(response.data?.data)
  },

  async getSummary(): Promise<ClientSummary> {
    const response = await http.get('/customers/summary')
    const data = response.data?.data || {}
    return {
      customersCount: Number(data.customers_count || 0),
      purchasesCount: Number(data.purchases_count || 0),
      netPurchasesTotal: numberValue(data.net_purchases_total),
    }
  },

  async getPurchases(
    id: number,
    params?: {
      page?: number
      per_page?: number
      date_from?: string
      date_to?: string
    },
  ): Promise<ClientPurchasesResult> {
    const response = await http.get(`/customers/${id}/purchases`, { params })
    const paginator = response.data?.data || {}
    return {
      data: ((paginator.data || []) as BackendPurchase[]).map((purchase) => ({
        id: purchase.id,
        ticketNumber: purchase.ticket_number,
        status: purchase.status,
        branchName: purchase.sucursal?.name || '',
        itemsCount: Number(purchase.items_count || 0),
        originalTotal: numberValue(purchase.original_total),
        refundedTotal: numberValue(purchase.refunded_total),
        netTotal: numberValue(purchase.net_total),
        completedAt: purchase.completed_at,
      })),
      currentPage: paginator.current_page || 1,
      lastPage: paginator.last_page || 1,
      perPage: paginator.per_page || params?.per_page || 10,
      total: paginator.total || 0,
      from: paginator.from,
      to: paginator.to,
    }
  },

  async createItem(payload: CreateClientPayload): Promise<ClientItem> {
    const formData = new FormData()
    appendPayload(formData, payload)
    const response = await http.post('/customers', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return mapClient(response.data?.data)
  },

  async updateItem(
    id: number,
    payload: UpdateClientPayload,
  ): Promise<ClientItem> {
    const formData = new FormData()
    formData.append('_method', 'PUT')
    appendPayload(formData, payload)
    const response = await http.post(`/customers/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return mapClient(response.data?.data)
  },

  async deleteItem(id: number): Promise<void> {
    await http.delete(`/customers/${id}`)
  },
}
